import { AtributeElement, AttributeColor, FacingGroup, Block, WosInterval } from './job.types';
import {
  STEP_AISLE_HEIGHT,
  TARGET_WOS,
  WOS_COLOR_HIGHEST,
  WOS_COLOR_LOWEST,
  WOS_COLOR_TARGET,
  WOS_INTERVAL_KEY_PREFIX,
  WOS_LEGEND_INTERVALS,
  WOS_LEGEND_SEGMENT_INTERVALS,
} from '../../utils/constants';
import { SEGMENT_WIDTH } from '../../components/ViewAndEdit/editorConstants';
import { getFacingHeight } from '../../components/ViewAndEdit/OptimizedAisleView/utils';

export function revertOrientationFacing(
  orientation: number,
  width: number,
  height: number,
  depth: number
) {
  switch (orientation) {
    case 1:
    case 7:
    case 13:
    case 19:
      return {
        width: height,
        height: width,
        depth
      };
    case 2:
    case 8:
    case 14:
    case 20:
      return {
        width: depth,
        height,
        depth: width
      };
    case 3:
    case 9:
    case 15:
    case 21:
      return {
        width: depth,
        height: width,
        depth: height
      };
    case 4:
    case 10:
    case 16:
    case 22:
      return {
        width,
        height: depth,
        depth: height
      };
    case 5:
    case 11:
    case 17:
    case 23:
      return {
        width: height,
        height: depth,
        depth: width
      };
    default:
      return {
        width,
        height,
        depth
      };
  }
}

export function rotateHorizontallyOrientationFacing(
  orientation: number,
) {
  switch (orientation) {
    case 0:
    case 6:
    case 12:
    case 18:
    case 24:
      return 2;
    case 1:
    case 7:
    case 13:
    case 19:
      return 5;
    case 3:
    case 9:
    case 15:
    case 21:
      return 4;
    case 4:
    case 10:
    case 16:
    case 22:
      return 3;
    case 5:
    case 11:
    case 17:
    case 23:
      return 1;
    default:
      return 0;
  }
}

export function rotateVerticallyOrientationFacing(
  orientation: number,
) {
  switch (orientation) {
    case 0:
    case 6:
    case 12:
    case 18:
    case 24:
      return 4;
    case 1:
    case 7:
    case 13:
    case 19:
      return 3;
    case 2:
    case 8:
    case 14:
    case 20:
      return 5;
    case 3:
    case 9:
    case 15:
    case 21:
      return 1;
    case 5:
    case 11:
    case 17:
    case 23:
      return 2;
    default:
      return 0;
  }
}

export function rotateClockwiseOrientationFacing(
  orientation: number,
) {
  switch (orientation) {
    case 0:
    case 6:
    case 12:
    case 18:
    case 24:
      return 1;
    case 2:
    case 8:
    case 14:
    case 20:
      return 3;
    case 3:
    case 9:
    case 15:
    case 21:
      return 2;
    case 4:
    case 10:
    case 16:
    case 22:
      return 5;
    case 5:
    case 11:
    case 17:
    case 23:
      return 4;
    default:
      return 0;
  }
}

export function rotateCappedOrientationFacing(
  orientation: number,
) {
  switch (orientation) {
    case 1:
    case 7:
    case 13:
    case 19:
      return 3;
    case 2:
    case 8:
    case 14:
    case 20:
      return 5;
    case 3:
    case 9:
    case 15:
    case 21:
      return 1;
    case 4:
    case 10:
    case 16:
    case 22:
      return 0;
    case 5:
    case 11:
    case 17:
    case 23:
      return 2;
    default:
      return 4;
  }
}

export function normalizeWos(wos: number | null | undefined): number {
  return typeof wos === 'number' && Number.isFinite(wos) && wos > 0 ? wos : 0;
}

const hexToRgb = (hex: string): [number, number, number] => {
  const value = parseInt(hex.replace('#', ''), 16);

  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const rgbToHex = (rgb: Array<number>): string =>
  `#${rgb.map((channel) => Math.round(channel).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

const clampUnit = (value: number) => Math.min(1, Math.max(0, value));

export function interpolateHexColor(from: string, to: string, ratio: number): string {
  const fromRgb = hexToRgb(from);
  const toRgb = hexToRgb(to);
  const t = clampUnit(ratio);

  return rgbToHex(fromRgb.map((channel, index) => channel + (toRgb[index] - channel) * t));
}

const logWos = (wos: number) => Math.log10(normalizeWos(wos) + 1);

export function getWosColor(wos: number, maxWos: number, target: number = TARGET_WOS): string {
  const value = logWos(wos);
  const targetValue = logWos(target);
  const maxValue = logWos(maxWos);

  if (value <= targetValue || maxValue <= targetValue) {
    const ratio = targetValue > 0 ? value / targetValue : 1;

    return interpolateHexColor(WOS_COLOR_LOWEST, WOS_COLOR_TARGET, ratio);
  }

  return interpolateHexColor(
    WOS_COLOR_TARGET,
    WOS_COLOR_HIGHEST,
    (value - targetValue) / (maxValue - targetValue)
  );
}

export function formatWosValue(wos: number): string {
  const value = normalizeWos(wos);
  const oneDecimal = value.toFixed(1);

  return +oneDecimal < 10 ? oneDecimal : Math.round(value).toString();
}

export function formatWosRange(min: number, max: number): string {
  const from = formatWosValue(min);
  const to = formatWosValue(max);

  return from === to ? from : `${from}–${to}`;
}

const unlogWos = (value: number) => Math.pow(10, value) - 1;

export function buildWosIntervals(maxWos: number, target: number = TARGET_WOS): Array<WosInterval> {
  const max = normalizeWos(maxWos);
  const segments = max > target
    ? [
      { from: 0, to: target, count: WOS_LEGEND_SEGMENT_INTERVALS },
      { from: target, to: max, count: WOS_LEGEND_INTERVALS - WOS_LEGEND_SEGMENT_INTERVALS },
    ]
    : [{ from: 0, to: target, count: WOS_LEGEND_INTERVALS }];
  const boundaries = segments.reduce<Array<number>>((acc, { from, to, count }) => {
    const logFrom = logWos(from);
    const logStep = (logWos(to) - logFrom) / count;

    return acc.concat(
      Array.from({ length: count }, (_, index) => index === count - 1 ? to : unlogWos(logFrom + logStep * (index + 1)))
    );
  }, [0]);

  return boundaries.slice(1).map((to, index) => {
    const from = boundaries[index];

    return {
      key: `${WOS_INTERVAL_KEY_PREFIX}${index + 1}`,
      from,
      to,
      midpoint: unlogWos((logWos(from) + logWos(to)) / 2),
      range: formatWosRange(from, to),
    };
  });
}

export function getWosIntervalIndex(wos: number, intervals: Array<WosInterval>): number {
  const value = normalizeWos(wos);
  const index = intervals.findIndex(({ to }) => value < to);

  return index === -1 ? intervals.length - 1 : index;
}

export function generateWosAttributes(
  wos: Record<string, number>,
  productsList: Array<string>,
  target: number = TARGET_WOS
): {
  products: Record<string, AtributeElement>;
  groups: Array<string>;
  colors: Record<string, AttributeColor>;
  ranges: Record<string, string>;
  empty: Array<string>;
} {
  const maxWos = productsList.reduce((max, productId) => Math.max(max, normalizeWos(wos[productId])), 0);
  const intervals = buildWosIntervals(maxWos, target);
  const groups = intervals.map(({ key }) => key);
  const products: Record<string, AtributeElement> = {};
  const colors: Record<string, AttributeColor> = {};
  const ranges: Record<string, string> = {};

  intervals.forEach(({ key, midpoint, range }) => {
    colors[key] = getWosColor(midpoint, maxWos, target);
    ranges[key] = range;
  });

  productsList.forEach((productId) => {
    products[productId] = {
      unique: (wos[productId] || 0).toFixed(2),
      name: intervals[getWosIntervalIndex(wos[productId], intervals)].key,
      color: getWosColor(wos[productId], maxWos, target),
    };
  });

  const usedGroups = Object.values(products).map(({ name }) => name);

  return { products, groups, colors, ranges, empty: groups.filter((key) => !usedGroups.includes(key)) };
}

export function generateRangeDGM(
  dgm: Record<string, number> | undefined,
  productsList: Array<string>,
): Record<string, AtributeElement> {
  const rangeDGM = productsList.reduce<Record<number, AtributeElement>>(
    (acc, productId) => {
      const productDGM = dgm?.[productId] || 0;
      let range;

      switch (true) {
        case productDGM < 0:
          range = {
            unique: productDGM.toFixed(2),
            name: 'DGM < 0'
          };
          break;
        case productDGM >= 0 && productDGM < 0.05:
          range = {
            unique: productDGM.toFixed(2),
            name: '0% <= DGM < 5%'
          };
          break;
        case productDGM >= 0.05 && productDGM < 0.15:
          range = {
            unique: productDGM.toFixed(2),
            name: '5% <= DGM < 15%'
          };
          break;
        case productDGM >= 0.15 && productDGM < 0.25:
          range = {
            unique: productDGM.toFixed(2),
            name: '15% <= DGM < 25%'
          };
          break;
        case productDGM >= 0.25 && productDGM < 0.5:
          range = {
            unique: productDGM.toFixed(2),
            name: '25% <= DGM < 50%'
          };
          break;
        case productDGM >= 0.5:
          range = {
            unique: productDGM.toFixed(2),
            name: 'DGM >= 50%'
          };
          break;
      }

      if (range) {
        return {
          ...acc,
          [productId.toString()]: range
        };
      }

      return acc;
    },
        {} as Record<string, AtributeElement>
  );

  return rangeDGM;
}

export function heightByStep(height: number) {
  return height % STEP_AISLE_HEIGHT !== 0
    ? STEP_AISLE_HEIGHT * (Math.trunc(height / STEP_AISLE_HEIGHT) + 1)
    : height;
}

// `packaging` and `cappedPackaging` are already oriented by `facingGroupsSelector`; do not rotate them again.
export function facingGroupHeight(facingGroup: FacingGroup) {
  return facingGroup.y + getFacingHeight(
    facingGroup.packaging.height,
    facingGroup.vertical_facings,
    facingGroup.cap_nested,
    facingGroup.packaging.nested_height,
    facingGroup.cappedPackaging?.height ?? 0,
    facingGroup.capped_vertical_facings
  );
}

export function updateBlocks(
  blocks: Array<Block>,
  segment: number,
  operation: number
): Array<Block> {
  return blocks.map((block, index) => {
    const from = index ? blocks[index - 1].num_segments + 1 : 1;
    const to = from + block.num_segments - 1;
    const numSegments = segment >= from && segment <= to
      ? block.num_segments + operation
      : block.num_segments;

    return {
      ...block,
      num_segments: numSegments,
      width: numSegments * SEGMENT_WIDTH,
    };
  });
}

export function orientationFacing(
  orientation: number,
  width: number,
  height: number,
  depth: number
) {
  switch (orientation) {
    case 1:
    case 7:
    case 13:
    case 19:
      return {
        width: height,
        height: width,
        depth
      };
    case 2:
    case 8:
    case 14:
    case 20:
      return {
        width: depth,
        height,
        depth: width
      };
    case 3:
    case 9:
    case 15:
    case 21:
      return {
        width: height,
        height: depth,
        depth: width
      };
    case 4:
    case 10:
    case 16:
    case 22:
      return {
        width,
        height: depth,
        depth: height
      };
    case 5:
    case 11:
    case 17:
    case 23:
      return {
        width: depth,
        height: width,
        depth: height
      };
    default:
      return {
        width,
        height,
        depth
      };
  }
}
